import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import style from "./index.module.scss"
import WrapperContent from "../../shared/WrapperContent";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import CloseIcon from "../../shared/icons/CloseIcon";
import InputCustom from "../../shared/forms/input-custom";
import UserIcon from "../../shared/icons/UserIcon";
import PhoneIcon from "../../shared/icons/PhoneIcon";
import EmailIcon from "../../shared/icons/EmailIcon";
import { Employee, User } from '../../../services';
import { requestErrorDisplay } from '../../../utils';
import InputMask from 'react-input-mask';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import Loading from '../../shared/Loading/Loading';
import cn from 'classnames';
import SelectCustom from '../../shared/forms/select-custom';

const PHONE_MASK = '+79999999999';

const WorkersCreatePage = () => {
  const { t } = useTranslation();
  const { user } = useSelector(state => state);
  const [searchParams] = useSearchParams()
  const navigate = useNavigate();
  const driversUrl = "/workers"
  const backUrl = searchParams.get("redirectUrl") ? searchParams.get("redirectUrl") : driversUrl
  const [isLoading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [surname, setSurName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [idNumber, setIdNumber] = useState("");
  const [emailError, setEmailError] = useState('');
  const [selectedRole, setSelectedRole] = useState(null);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [showEmailError, setShowEmailError] = useState(false);

  const roles = [
    { title: t('workersCreatePage.roles.employee'), value: 'employee' },
    { title: t('workersCreatePage.roles.admin'), value: 'admin' },
  ];

  const validateFormatData = () => {
    if (!name.trim()) {
      toast.error(t('workersCreatePage.validation.invalidName'));
      return false
    }

    if (!surname.trim()) {
      toast.error(t('workersCreatePage.validation.invalidLastName'));
      return false
    }

    const phoneDigits = phoneNumber.replace(/\D/g, '');
    if (phoneDigits.length < 11) {
      toast.error(t('workersCreatePage.validation.invalidPhone'));
      return false
    }

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      toast.error(t('workersCreatePage.validation.invalidEmail'));
      return false
    }

    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(trimmedEmail)) {
      toast.error(t('confirmEmailComponent.validation.invalidEmailFormat'));
      return false;
    }

    return true
  }

  const checkEmailExists = async (emailToCheck) => {
    if (!emailToCheck) return { exists: false, error: null };

    const trimmedEmail = emailToCheck.trim();
    if (!trimmedEmail) return { exists: false, error: null };

    setIsCheckingEmail(true);
    setEmailError('');

    try {
      const response = await User.check("email", trimmedEmail);

      const exists = response.data?.isExists || false;

      if (exists) {
        const errorMessage = t('workersCreatePage.toast.emailExists');
        setEmailError(errorMessage);
        return { exists: true, error: errorMessage };
      }

      return { exists: false, error: null };
    } catch (error) {
      console.error('Error checking email:', error);
      const errorMessage = t('workersCreatePage.toast.emailCheckError');
      setEmailError(errorMessage);
      return { exists: false, error: errorMessage };
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const onSubmit = async () => {
    if (user && !user.permissions?.includes("employer_add")) {
      toast.info(t('common.noAccessSupportMessage'), {
        autoClose: 10000
      });
      return;
    }

    setShowEmailError(true);

    if (!validateFormatData()) {
      return false;
    }

    const emailCheckResult = await checkEmailExists(email);
    if (emailCheckResult.exists) {
      toast.error(emailCheckResult.error);
      return false;
    }

    if (emailCheckResult.error) {
      toast.error(emailCheckResult.error);
      return false;
    }

    setLoading(true);
    const trimmedEmail = email.trim();
    const data = {
      "companyId": user.id,
      "companyName": user.name,
      "type": user.type,
      "name": name.trim(),
      "surname": surname.trim(),
      "email": trimmedEmail,
      "phoneNumber": phoneNumber.replace('+', ''),
      // "role": role
    };

    Employee.createEmployee(data)
      .then(res => res.data)
      .then(async data => {
        console.log('data', data)
        toast.success(t('workersCreatePage.toast.createSuccess'));
        toast.info(t('workersCreatePage.toast.activationSent', { email: trimmedEmail }), { delay: 2000 });
        navigate(backUrl)
      })
      .catch(error => {
        requestErrorDisplay(error)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <WrapperContent>
      <div className={style.wrapper}>
        <h1 className={style.title}>{t('workersCreatePage.title')}</h1>
        <div className={style.content}>
          <div className={style.field}>
            <span className={style.field_label}>{t('workersCreatePage.name.label')}</span>
            <InputCustom
              placeholder={t('workersCreatePage.name.placeholder')}
              defaultValue={name}
              onChangeValue={setName}
              prefixStart={<UserIcon />}
            />
          </div>
          <div className={style.field}>
            <span className={style.field_label}>{t('workersCreatePage.lastName.label')}</span>
            <InputCustom
              placeholder={t('workersCreatePage.lastName.placeholder')}
              defaultValue={surname}
              onChangeValue={setSurName}
              prefixStart={<UserIcon />}
            />
          </div>

          <div className={style.field}>
            <span className={style.field_label}>{t('workersCreatePage.email.label')}</span>
            <InputCustom
              defaultValue={email}
              onChangeValue={setEmail}
              placeholder={t('workersCreatePage.email.placeholder')}
              prefixStart={<EmailIcon />}
              className={showEmailError && emailError ? style.input_error : ''}
            />
            {showEmailError && emailError && (
              <p className={style.error_message}>{emailError}</p>
            )}
          </div>

          <div className={style.field}>
            <span className={style.field_label}>{t('workersCreatePage.phone.label')}</span>
            <InputMask
              value={phoneNumber}
              maskChar={null}
              mask={PHONE_MASK}
              onChange={(event) => setPhoneNumber(event.target.value)}
              disabled={false}
            >
              {(inputProps) => (
                <InputCustom
                  {...inputProps}
                  placeholder={PHONE_MASK}
                  prefixStart={<PhoneIcon />}
                  defaultValue={phoneNumber}
                  onChangeValue={setPhoneNumber}
                />
              )}
            </InputMask>
          </div>

          <Link to={backUrl} className={style.buttonClose}>
            <CloseIcon />
          </Link>
        </div>
        <div className={style.block}>
          <Link to={backUrl} className={style.buttonCancel}>
            {t('workersCreatePage.buttons.cancel')}
          </Link>

          <button
            className={cn(style.button_upload, {
              [style.button_upload_loading]: isLoading,
            })}
            onClick={() => onSubmit()}
            disabled={isLoading || isCheckingEmail}
          >
            {!isLoading && <span>{t('workersCreatePage.buttons.create')}</span>}
            {isLoading && <Loading inBlock marginBottom={0} />}
          </button>
        </div>
      </div>
    </WrapperContent>
  );
};

export default WorkersCreatePage;
